import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useHelpdeskLanguage } from '../../context/HelpdeskLanguageContext';
import './Helpdesk.css';
import AttachmentDropzone from '../../components/AttachmentDropzone';
import ScreenRecorder, { canRecordScreen } from '../../components/ScreenRecorder';
import LinkifiedText from '../../components/LinkifiedText';

// Translations
const translations = {
    en: {
        backToDashboard: 'Back to Dashboard',
        ticketInfo: 'TICKET INFORMATION',
        status: 'Status',
        priority: 'Priority',
        category: 'Category',
        department: 'Department',
        created: 'Created',
        updated: 'Updated',
        createdBy: 'Created By',
        createdByClient: 'Client',
        createdByStaff: 'Staff',
        clientInfo: 'CLIENT INFORMATION',
        erpOrigin: 'RAISED FROM MATIX ERP',
        erpUser: 'User',
        erpTenant: 'Tenant DB',
        erpSubsidiary: 'Subsidiary',
        name: 'Name',
        email: 'Email',
        company: 'Company',
        assignedAgents: 'ASSIGNED AGENTS',
        noAgents: 'No agents assigned',
        editHistory: 'EDIT HISTORY',
        show: 'Show',
        hide: 'Hide',
        noHistory: 'No edit history',
        loading: 'Loading...',
        reply: 'Reply',
        internalNote: 'Internal Note',
        typeReply: 'Type your reply...',
        sendReply: 'Send Reply',
        takeTicket: 'Take Ticket',
        assign: 'Assign',
        delete: 'Delete',
        edit: 'Edit',
        cancel: 'Cancel',
        save: 'Save',
        confirmDelete: 'Are you sure you want to delete this ticket?',
        deleteWarning: 'This action cannot be undone.',
        ticketNotFound: 'Ticket not found',
        none: 'None',
        cannedResponses: 'Canned Responses',
        selectCanned: 'Select a canned response...',
        assignAgent: 'Assign Agent',
        selectAgent: 'Select an agent...',
        close: 'Close',
        unsavedChanges: 'You have unsaved changes',
        deleteTicket: 'Delete Ticket',
        confirmDeleteMessage: 'Are you sure you want to delete this ticket? This action cannot be undone.',
        confirmDeleteReply: 'Are you sure you want to delete this reply?',
        deleting: 'Deleting...',
        sending: 'Sending...',
        // Status options
        statusOpen: 'Open',
        statusAssigned: 'Assigned',
        statusInProgress: 'In Progress',
        statusWaiting: 'Waiting for Customer',
        statusSolvedPending: 'Resolved – Pending Update',
        statusResolved: 'Resolved',
        statusClosed: 'Closed',
        // Priority options
        priorityLow: 'Low',
        priorityMedium: 'Medium',
        priorityHigh: 'High',
        priorityCritical: 'Critical',
        // Extra
        attachments: 'Attachments',
        allAttachments: 'All Attachments',
        enterCompany: 'Enter company name',
        conversation: 'Conversation',
        description: 'Description',
        by: 'By',
        unknown: 'Unknown',
        agent: 'Agent',
        // Success/Error messages
        replyDeleted: 'Reply deleted successfully!',
        replySent: 'Reply sent successfully!',
        changesSaved: 'Changes saved successfully!',
        ticketAssigned: 'Ticket assigned successfully!',
        ticketAssignedToYou: 'Ticket assigned to you!',
        ticketDeleted: 'Ticket deleted successfully!',
        agentUnassigned: 'Agent unassigned successfully!',
        failedDeleteReply: 'Failed to delete reply',
        failedSendReply: 'Failed to send reply',
        failedSaveChanges: 'Failed to save changes',
        failedAssignTicket: 'Failed to assign ticket',
        failedUnassignAgent: 'Failed to unassign agent',
        failedDeleteTicket: 'Failed to delete ticket',
        pleaseSelectAgent: 'Please select an agent',
        willReopenTicket: '(This will reopen the ticket)'
    },
    ar: {
        backToDashboard: 'العودة للوحة التحكم',
        ticketInfo: 'معلومات التذكرة',
        status: 'الحالة',
        priority: 'الأولوية',
        category: 'التصنيف',
        department: 'القسم',
        created: 'تاريخ الإنشاء',
        updated: 'آخر تحديث',
        createdBy: 'أنشئت بواسطة',
        createdByClient: 'العميل',
        createdByStaff: 'الموظف',
        clientInfo: 'معلومات العميل',
        erpOrigin: 'وردت من ماتكس ERP',
        erpUser: 'المستخدم',
        erpTenant: 'قاعدة بيانات العميل',
        erpSubsidiary: 'الشركة',
        name: 'الاسم',
        email: 'البريد الإلكتروني',
        company: 'الشركة',
        assignedAgents: 'الموظفين المعينين',
        noAgents: 'لا يوجد موظفين معينين',
        editHistory: 'سجل التعديلات',
        show: 'عرض',
        hide: 'إخفاء',
        noHistory: 'لا يوجد سجل تعديلات',
        loading: 'جاري التحميل...',
        reply: 'الرد',
        internalNote: 'ملاحظة داخلية',
        typeReply: 'اكتب ردك هنا...',
        sendReply: 'إرسال الرد',
        takeTicket: 'استلام التذكرة',
        assign: 'إسناد إلى',
        delete: 'حذف',
        edit: 'تعديل',
        cancel: 'إلغاء',
        save: 'حفظ',
        confirmDelete: 'هل أنت متأكد من حذف هذه التذكرة؟',
        deleteWarning: 'هذا الإجراء لا يمكن التراجع عنه.',
        ticketNotFound: 'التذكرة غير موجودة',
        none: 'لا يوجد',
        cannedResponses: 'الردود الجاهزة',
        selectCanned: 'اختر رد جاهز...',
        assignAgent: 'تعيين موظف',
        selectAgent: 'اختر موظف...',
        close: 'إغلاق',
        unsavedChanges: 'لديك تغييرات غير محفوظة',
        deleteTicket: 'حذف التذكرة',
        confirmDeleteMessage: 'هل أنت متأكد من حذف هذه التذكرة؟ لا يمكن التراجع عن هذا الإجراء.',
        confirmDeleteReply: 'هل أنت متأكد من حذف هذا الرد؟',
        deleting: 'جاري الحذف...',
        sending: 'جاري الإرسال...',
        // Status options
        statusOpen: 'مفتوحة',
        statusAssigned: 'تم الإسناد',
        statusInProgress: 'قيد التنفيذ',
        statusWaiting: 'بانتظار العميل',
        statusSolvedPending: 'تم الحل وفي انتظار التحديث',
        statusResolved: 'تم الحل والتسليم',
        statusClosed: 'مغلقة',
        // Priority options
        priorityLow: 'منخفضة',
        priorityMedium: 'متوسطة',
        priorityHigh: 'عالية',
        priorityCritical: 'حرجة',
        // Extra
        attachments: 'المرفقات',
        allAttachments: 'جميع المرفقات',
        enterCompany: 'أدخل اسم الشركة',
        conversation: 'المحادثة',
        description: 'الوصف',
        by: 'بواسطة',
        unknown: 'غير معروف',
        agent: 'موظف',
        // Success/Error messages
        replyDeleted: 'تم حذف الرد بنجاح!',
        replySent: 'تم إرسال الرد بنجاح!',
        changesSaved: 'تم حفظ التغييرات بنجاح!',
        ticketAssigned: 'تم تعيين التذكرة بنجاح!',
        ticketAssignedToYou: 'تم تعيين التذكرة لك!',
        ticketDeleted: 'تم حذف التذكرة بنجاح!',
        agentUnassigned: 'تم إلغاء تعيين الموظف بنجاح!',
        failedDeleteReply: 'فشل حذف الرد',
        failedSendReply: 'فشل إرسال الرد',
        failedSaveChanges: 'فشل حفظ التغييرات',
        failedAssignTicket: 'فشل تعيين التذكرة',
        failedUnassignAgent: 'فشل إلغاء تعيين الموظف',
        failedDeleteTicket: 'فشل حذف التذكرة',
        pleaseSelectAgent: 'الرجاء اختيار موظف',
        willReopenTicket: '(سيتم إعادة فتح التذكرة)'
    }
};

function HelpdeskTicketDetail() {
    const { id } = useParams();
    const { language, isRTL } = useHelpdeskLanguage();
    const t = (key) => translations[language]?.[key] || translations.en[key] || key;
    
    const [ticket, setTicket] = useState(null);
    const [notes, setNotes] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [assignedAgents, setAssignedAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [replyMessage, setReplyMessage] = useState('');
    const [replyFiles, setReplyFiles] = useState([]);
    const [isInternal, setIsInternal] = useState(false);
    const [replyLoading, setReplyLoading] = useState(false);
    const [agents, setAgents] = useState([]);
    const [categories, setCategories] = useState([]);
    const [cannedResponses, setCannedResponses] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [assignLoading, setAssignLoading] = useState(false);
    const [unassignLoading, setUnassignLoading] = useState(null);
    
    // Editable fields
    const [editStatus, setEditStatus] = useState('');
    const [editPriority, setEditPriority] = useState('');
    const [editCategory, setEditCategory] = useState('');
    const [editDepartment, setEditDepartment] = useState('');
    const [editCompany, setEditCompany] = useState('');
    const [editSubject, setEditSubject] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [hasChanges, setHasChanges] = useState(false);
    
    // Departments
    const [departments, setDepartments] = useState([]);
    
    // Delete modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    
    // Edit mode for subject/description
    const [isEditingSubject, setIsEditingSubject] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    
    // History
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    
    // Delete reply
    const [deleteReplyLoading, setDeleteReplyLoading] = useState(null);

    const token = localStorage.getItem('token');

    // Screen recording on a reply, stored in Matix Shot; only its id travels with the reply.
    const [recordingConfig, setRecordingConfig] = useState(null);
    const [showRecorder, setShowRecorder] = useState(false);
    const [replyRecording, setReplyRecording] = useState(null);

    useEffect(() => {
        if (!canRecordScreen()) return;
        fetch('/api/v1/helpdesk-agent/recordings/config', { headers: { 'Authorization': `Bearer ${token}` } })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => data?.enabled && setRecordingConfig(data))
            .catch(() => {});
    }, [token]);

    useEffect(() => () => replyRecording && URL.revokeObjectURL(replyRecording.previewUrl), [replyRecording]);

    const reserveRecording = async (body) => {
        const response = await fetch('/api/v1/helpdesk-agent/recordings', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) throw Object.assign(new Error('reserve failed'), { serverMessage: data.message });
        return data.recording;
    };
// Get current user info from token
    const currentUser = (() => {
        try {
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                return { id: payload.id, role: payload.role || 'agent' };
            }
        } catch (e) {
            console.error('Error parsing token:', e);
        }
        return { id: null, role: 'agent' };
    })();

    // Check if current user can edit subject/description
    // Allowed: admin, supervisor, or assigned agent
    const canEditContent = currentUser.role === 'admin' 
        || currentUser.role === 'supervisor' 
        || assignedAgents.some(a => a.agent_id === currentUser.id);
        
    // Redirect to login if no token
    useEffect(() => {
        if (!token) {
            window.location.href = '/login';
        }
    }, [token]);

    useEffect(() => {
        if (!token) return;
        loadTicket();
        loadCategories();
        loadDepartments();
        loadCannedResponses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Load agents when ticket department changes
    useEffect(() => {
        if (ticket?.department_id) {
            loadAgents(ticket.department_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticket?.department_id]);

    // Reload agents when department is changed in edit mode
    useEffect(() => {
        if (editDepartment && editDepartment !== ticket?.department_id) {
            loadAgents(editDepartment);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editDepartment]);

    // Update editable fields when ticket loads
    useEffect(() => {
        if (ticket) {
            setEditStatus(ticket.status || '');
            setEditPriority(ticket.priority || '');
            setEditCategory(ticket.category_id || '');
            setEditDepartment(ticket.department_id || '');
            setEditCompany(ticket.client_company || '');
            setEditSubject(ticket.subject || '');
            setEditDescription(ticket.description || '');
            setHasChanges(false);
            setIsEditingSubject(false);
            setIsEditingDescription(false);
        }
    }, [ticket]);

    const loadTicket = async () => {
        try {
            setLoading(true);
            const response = await fetch(`/api/v1/helpdesk-agent/tickets/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // Check for auth errors
            if (response.status === 401 || response.status === 403) {
                localStorage.removeItem('token');
                window.location.href = '/login';
                return;
            }

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setTicket(data.ticket);
                    setNotes(data.notes || []);
                    setAttachments(data.attachments || []);
                    setAssignedAgents(data.assignedAgents || []);
                }
            }
        } catch (error) {
            console.error('Failed to load ticket:', error);
            // Network error - redirect to login
            if (error.name === 'TypeError' || error.message?.includes('fetch')) {
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
        } finally {
            setLoading(false);
        }
    };

    const loadAgents = async (departmentId) => {
        try {
            let url = '/api/v1/helpdesk-agent/agents';
            if (departmentId) {
                url += `?department_id=${departmentId}`;
            }
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setAgents(data.agents || []);
                }
            }
        } catch (error) {
            console.error('Failed to load agents:', error);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await fetch('/api/v1/helpdesk-agent/categories', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setCategories(data.categories || []);
                }
            }
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const loadDepartments = async () => {
        try {
            const response = await fetch('/api/v1/helpdesk-agent/departments/active', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setDepartments(data.departments || []);
                }
            }
        } catch (error) {
            console.error('Failed to load departments:', error);
        }
    };

    const loadCannedResponses = async () => {
        try {
            const response = await fetch('/api/v1/helpdesk-agent/canned-responses', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setCannedResponses(data.responses || []);
                }
            }
        } catch (error) {
            console.error('Failed to load canned responses:', error);
        }
    };

    const loadHistory = async () => {
        try {
            setHistoryLoading(true);
            const response = await fetch(`/api/v1/helpdesk-agent/tickets/${id}/history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setHistory(data.history || []);
                }
            }
        } catch (error) {
            console.error('Failed to load history:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleDeleteReply = async (noteId) => {
        if (!window.confirm(t('confirmDeleteReply'))) {
            return;
        }
        
        try {
            setDeleteReplyLoading(noteId);
            const response = await fetch(`/api/v1/helpdesk-agent/tickets/${id}/notes/${noteId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    // Remove from UI immediately
                    setNotes(prev => prev.filter(n => n.id !== noteId));
                    setSaveMessage(t('replyDeleted'));
                    setTimeout(() => setSaveMessage(''), 3000);
                }
            } else {
                const data = await response.json();
                setSaveMessage(data.message || t('failedDeleteReply'));
                setTimeout(() => setSaveMessage(''), 3000);
            }
        } catch (error) {
            console.error('Failed to delete reply:', error);
            setSaveMessage(t('failedDeleteReply'));
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setDeleteReplyLoading(null);
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyMessage.trim() && !replyRecording) return;

        try {
            setReplyLoading(true);
            const formData = new FormData();
            formData.append('message', replyMessage);
            formData.append('is_internal', isInternal);
            replyFiles.forEach(file => formData.append('attachments', file));
            if (replyRecording) formData.append('recording_id', replyRecording.id);

            const response = await fetch(`/api/v1/helpdesk-agent/tickets/${id}/reply`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setReplyMessage('');
                    setIsInternal(false);
                    setReplyFiles([]);
                    setReplyRecording(null);
                    loadTicket();
                    setSaveMessage(t('replySent'));
                    setTimeout(() => setSaveMessage(''), 3000);
                }
            }
        } catch (error) {
            console.error('Failed to send reply:', error);
            alert(t('failedSendReply'));
        } finally {
            setReplyLoading(false);
        }
    };

    const handleFieldChange = (field, value) => {
        switch(field) {
            case 'status':
                setEditStatus(value);
                break;
            case 'priority':
                setEditPriority(value);
                break;
            case 'category':
                setEditCategory(value);
                break;
            case 'department':
                setEditDepartment(value);
                break;
            case 'company':
                setEditCompany(value);
                break;
            case 'subject':
                setEditSubject(value);
                break;
            case 'description':
                setEditDescription(value);
                break;
            default:
                break;
        }
        setHasChanges(true);
    };

    const handleSaveChanges = async () => {
        try {
            setSaving(true);
            
            // Update ticket info (status, priority, category, department, subject, description)
            const ticketUpdateData = {};
            if (editStatus !== ticket.status) ticketUpdateData.status = editStatus;
            if (editPriority !== ticket.priority) ticketUpdateData.priority = editPriority;
            if (editCategory !== (ticket.category_id || '')) ticketUpdateData.category_id = editCategory || null;
            if (editDepartment !== (ticket.department_id || '')) ticketUpdateData.department_id = editDepartment || null;
            if (editSubject !== ticket.subject) ticketUpdateData.subject = editSubject;
            if (editDescription !== ticket.description) ticketUpdateData.description = editDescription;
            
            if (Object.keys(ticketUpdateData).length > 0) {
                const response = await fetch(`/api/v1/helpdesk-agent/tickets/${id}`, {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(ticketUpdateData)
                });
                
                if (!response.ok) {
                    throw new Error('Failed to update ticket');
                }
            }
            
            // Update client company if changed
            if (editCompany !== (ticket.client_company || '')) {
                const response = await fetch(`/api/v1/helpdesk-agent/tickets/${ticket.id}/company`, {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ company_name: editCompany })
                });
                
                if (!response.ok) {
                    console.warn('Failed to update ticket company');
                }
            }
            
            setSaveMessage(t('changesSaved'));
            setHasChanges(false);
            setIsEditingSubject(false);
            setIsEditingDescription(false);
            loadTicket();
            // Reload history if it's being shown
            if (showHistory) {
                loadHistory();
            }
            setTimeout(() => setSaveMessage(''), 3000);
        } catch (error) {
            console.error('Failed to save changes:', error);
            setSaveMessage(t('failedSaveChanges'));
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setSaving(false);
        }
    };

    const handleTakeTicket = async () => {
        try {
            const response = await fetch(`/api/v1/helpdesk-agent/tickets/${id}/take`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                loadTicket();
                setSaveMessage(t('ticketAssignedToYou'));
                setTimeout(() => setSaveMessage(''), 3000);
            }
        } catch (error) {
            console.error('Failed to take ticket:', error);
        }
    };

    const handleAssignTicket = async () => {
        if (!selectedAgent) {
            setSaveMessage(t('pleaseSelectAgent'));
            setTimeout(() => setSaveMessage(''), 3000);
            return;
        }
        
        try {
            setAssignLoading(true);
            const response = await fetch(`/api/v1/helpdesk-agent/tickets/${id}/assign`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ agent_id: parseInt(selectedAgent) })
            });

            const data = await response.json();
            
            if (response.ok && data.success) {
                // Find the agent name from the agents list
                const assignedAgent = agents.find(a => a.id === parseInt(selectedAgent));
                
                // Immediately update the UI
                if (assignedAgent) {
                    setAssignedAgents(prev => [...prev, {
                        id: Date.now(), // temporary ID
                        agent_id: parseInt(selectedAgent),
                        agent_name: assignedAgent.full_name
                    }]);
                }
                
                // Close modal and reset
                setShowAssignModal(false);
                setSelectedAgent('');
                
                // Show success message
                setSaveMessage(t('ticketAssigned'));
                setTimeout(() => setSaveMessage(''), 3000);
                
                // Reload ticket data in background to get accurate data
                loadTicket();
            } else {
                setSaveMessage(data.message || t('failedAssignTicket'));
                setTimeout(() => setSaveMessage(''), 3000);
            }
        } catch (error) {
            console.error('Failed to assign ticket:', error);
            setSaveMessage(t('failedAssignTicket'));
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setAssignLoading(false);
        }
    };

    const handleUnassign = async (agentId) => {
        try {
            setUnassignLoading(agentId);
            const response = await fetch(`/api/v1/helpdesk-agent/tickets/${id}/unassign`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ agent_id: agentId })
            });

            const data = await response.json();
            
            if (response.ok && data.success) {
                // Immediately remove from UI
                setAssignedAgents(prev => prev.filter(a => a.agent_id !== agentId));
                setSaveMessage(t('agentUnassigned'));
                setTimeout(() => setSaveMessage(''), 3000);
                
                // Reload in background
                loadTicket();
            } else {
                setSaveMessage(data.message || t('failedUnassignAgent'));
                setTimeout(() => setSaveMessage(''), 3000);
            }
        } catch (error) {
            console.error('Failed to unassign:', error);
            setSaveMessage(t('failedUnassignAgent'));
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setUnassignLoading(null);
        }
    };

    const handleDeleteTicket = async () => {
        try {
            setDeleteLoading(true);
            const response = await fetch(`/api/v1/helpdesk-agent/tickets/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setSaveMessage(t('ticketDeleted'));
                setTimeout(() => {
                    window.location.href = '/helpdesk';
                }, 1000);
            } else {
                const data = await response.json();
                setSaveMessage(data.message || t('failedDeleteTicket'));
                setTimeout(() => setSaveMessage(''), 3000);
            }
        } catch (error) {
            console.error('Failed to delete ticket:', error);
            setSaveMessage(t('failedDeleteTicket'));
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setDeleteLoading(false);
            setShowDeleteModal(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFieldLabel = (fieldName) => {
        const labels = {
            en: {
                'subject': 'Subject',
                'description': 'Description',
                'status': 'Status',
                'priority': 'Priority',
                'category_id': 'Category',
                'department_id': 'Department'
            },
            ar: {
                'subject': 'العنوان',
                'description': 'الوصف',
                'status': 'الحالة',
                'priority': 'الأولوية',
                'category_id': 'الفئة',
                'department_id': 'القسم'
            }
        };
        return labels[language]?.[fieldName] || labels.en[fieldName] || fieldName;
    };

    const truncateText = (text, maxLength = 100) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getAttachmentUrl = (attachment) => {
        // Extract filename from file_path and construct URL
        const filename = attachment.file_path.split('/').pop();
        return `/uploads/helpdesk/${filename}`;
    };

    const getAttachmentsForNote = (noteId) => {
        return attachments.filter(att => att.note_id === noteId);
    };

    const getTicketAttachments = () => {
        return attachments.filter(att => !att.note_id);
    };

    if (loading) {
        return (
            <Layout>
                <div className="hd-loading">{t('loading')}</div>
            </Layout>
        );
    }

    if (!ticket) {
        return (
            <Layout>
                <div className="hd-error">{t('ticketNotFound')}</div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="hd-ticket-detail" dir={isRTL ? 'rtl' : 'ltr'}>
                {/* Success/Error Message */}
                {saveMessage && (
                    <div className={`hd-save-message ${saveMessage.includes('Failed') ? 'error' : 'success'}`}>
                        {saveMessage}
                    </div>
                )}

                <div className="hd-detail-header">
                    <Link to="/helpdesk" className="hd-back-link">← {t('backToDashboard')}</Link>
                    <div className="hd-ticket-title">
                        <h1>#{ticket.ticket_number}</h1>
                        {isEditingSubject ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                                <input
                                    type="text"
                                    value={editSubject}
                                    onChange={(e) => handleFieldChange('subject', e.target.value)}
                                    className="hd-input"
                                    style={{ fontSize: '18px', fontWeight: 600, flex: 1 }}
                                    autoFocus
                                />
                                <button 
                                    onClick={() => { setIsEditingSubject(false); setEditSubject(ticket.subject); setHasChanges(false); }}
                                    className="hd-btn hd-btn-secondary hd-btn-sm"
                                >
                                    {t('cancel')}
                                </button>
                            </div>
                        ) : (
                        <h2 style={{ cursor: canEditContent ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={() => canEditContent && setIsEditingSubject(true)}>
                            {ticket.subject}
                            {canEditContent && <span style={{ fontSize: '14px', color: '#666' }}>✏️</span>}
                        </h2>
                        )}

                    </div>
                    <div className="hd-ticket-actions">
                        <button onClick={handleTakeTicket} className="hd-btn hd-btn-primary">
                            {t('takeTicket')}
                        </button>
                        <button onClick={() => setShowAssignModal(true)} className="hd-btn hd-btn-secondary">
                            {t('assign')}
                        </button>
                        <button 
                            onClick={() => setShowDeleteModal(true)} 
                            className="hd-btn"
                            style={{ backgroundColor: '#ef4444', color: 'white' }}
                        >
                            🗑️ {t('delete')}
                        </button>
                    </div>
                </div>

                <div className="hd-detail-content">
                    {/* Left Column - Conversation */}
                    <div className="hd-conversation-section">
                        {/* Save Changes Bar - appears when editing subject or description */}
                        {(isEditingSubject || isEditingDescription) && hasChanges && (
                            <div style={{ 
                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)', 
                                padding: '12px 16px', 
                                borderRadius: '8px', 
                                marginBottom: '15px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <span style={{ color: 'white', fontWeight: 500 }}>
                                    ✏️ {t('unsavedChanges')}
                                </span>
                                <button 
                                    onClick={handleSaveChanges}
                                    style={{ 
                                        backgroundColor: 'transparent', 
                                        color: 'white', 
                                        border: '2px solid white',
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '18px'
                                    }}
                                    disabled={saving}
                                    title="Save Changes"
                                >
                                    {saving ? '⏳' : '💾'}
                                </button>
                            </div>
                        )}
                        {/* Original Message */}
                        <div className="hd-message hd-message-client">
                            <div className="hd-message-header">
                                <span className="hd-message-author">👤 {ticket.client_name}</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span className="hd-message-date">{formatDate(ticket.created_at)}</span>
                                    {!isEditingDescription && canEditContent && (
                                        <button 
                                            onClick={() => setIsEditingDescription(true)}
                                            style={{ 
                                                background: 'rgba(255,255,255,0.1)', 
                                                border: 'none', 
                                                padding: '4px 8px', 
                                                borderRadius: '4px', 
                                                cursor: 'pointer',
                                                color: '#888',
                                                fontSize: '12px'
                                            }}
                                            title="Edit description"
                                        >
                                            ✏️ {t('edit')}
                                        </button>
                                    )}
                                </div>
                            </div>
                            {isEditingDescription ? (
                                <div style={{ marginTop: '10px' }}>
                                    <textarea
                                        value={editDescription}
                                        onChange={(e) => handleFieldChange('description', e.target.value)}
                                        className="hd-textarea"
                                        rows={6}
                                        autoFocus
                                    />
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                        <button 
                                            onClick={() => { setIsEditingDescription(false); setEditDescription(ticket.description); setHasChanges(false); }}
                                            className="hd-btn hd-btn-secondary hd-btn-sm"
                                        >
                                            {t('cancel')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="hd-message-body">
                                    <LinkifiedText text={ticket.description} />
                                </div>
                            )}
                            
                            {/* Ticket-level attachments */}
                            {getTicketAttachments().length > 0 && (
                                <div className="hd-attachments">
                                    <strong>📎 {t('attachments')}:</strong>
                                    <ul className="hd-attachment-list">
                                        {getTicketAttachments().map(att => (
                                            <li key={att.id}>
                                                <a href={getAttachmentUrl(att)} target="_blank" rel="noopener noreferrer">
                                                    {att.file_name}
                                                </a>
                                                <span className="hd-file-size">({formatFileSize(att.file_size)})</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Notes/Replies */}
                        {notes.map(note => (
                            <div 
                                key={note.id} 
                                className={`hd-message ${
                                    note.author_type === 'client' ? 'hd-message-client' : 
                                    note.visibility === 'internal' ? 'hd-message-internal' : 
                                    'hd-message-agent'
                                }`}
                            >
                                <div className="hd-message-header">
                                    <span className="hd-message-author">
                                        {note.author_type === 'client' ? '👤' : '🎧'} {note.author_name || t('agent')}
                                        {note.visibility === 'internal' && <span className="hd-internal-badge">Internal</span>}
                                    </span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span className="hd-message-date">{formatDate(note.created_at)}</span>
                                        {/* Delete button - for staff/agent replies (backend verifies ownership) */}
                                        {(note.author_type === 'agent' || note.author_type === 'staff') && (
                                            <button
                                                onClick={() => handleDeleteReply(note.id)}
                                                disabled={deleteReplyLoading === note.id}
                                                style={{
                                                    background: 'rgba(239, 68, 68, 0.2)',
                                                    border: 'none',
                                                    color: '#ef4444',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                                title="Delete this reply"
                                            >
                                                {deleteReplyLoading === note.id ? '...' : '🗑️'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="hd-message-body"><LinkifiedText text={note.message} /></div>
                                
                                {/* Note attachments */}
                                {getAttachmentsForNote(note.id).length > 0 && (
                                    <div className="hd-attachments">
                                        <strong>📎 {t('attachments')}:</strong>
                                        <ul className="hd-attachment-list">
                                            {getAttachmentsForNote(note.id).map(att => (
                                                <li key={att.id}>
                                                    <a href={getAttachmentUrl(att)} target="_blank" rel="noopener noreferrer">
                                                        {att.file_name}
                                                    </a>
                                                    <span className="hd-file-size">({formatFileSize(att.file_size)})</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Reply Form */}
                        <form onSubmit={handleReply} className="hd-reply-form">
                            <div className="hd-reply-header">
                                <h3>{t('reply')} {ticket.status === 'closed' && <span className="hd-reopen-note">{t('willReopenTicket')}</span>}</h3>
                                <label className="hd-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={isInternal}
                                        onChange={(e) => setIsInternal(e.target.checked)}
                                    />
                                    {t('internalNote')}
                                </label>
                            </div>
                            
                            {cannedResponses.length > 0 && (
                                <div className="hd-canned-responses">
                                    <select 
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                setReplyMessage(prev => prev + e.target.value);
                                                e.target.value = '';
                                            }
                                        }}
                                        className="hd-select"
                                    >
                                        <option value="">{t('selectCanned')}</option>
                                        {cannedResponses.map(resp => (
                                            <option key={resp.id} value={resp.content}>{resp.title}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <textarea
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                placeholder={t('typeReply')}
                                rows={5}
                                className="hd-textarea"
                            />
                            {/* Attachments — the client portal's box (components/AttachmentDropzone) */}
                            <AttachmentDropzone files={replyFiles} onChange={setReplyFiles} language={language} />

                            {recordingConfig && !replyRecording && (
                                <button type="button" className="hd-btn hd-record-btn" onClick={() => setShowRecorder(true)}>
                                    🎥 {language === 'ar' ? 'تسجيل فيديو للشاشة' : 'Record screen video'}
                                </button>
                            )}
                            {replyRecording && (
                                <div className="hd-recording-attachment">
                                    <video src={replyRecording.previewUrl} controls playsInline />
                                    <span className="hd-recording-name">
                                        🎥 {language === 'ar' ? 'تسجيل الشاشة' : 'Screen recording'} · {(replyRecording.size / (1024 * 1024)).toFixed(1)} MB
                                    </span>
                                    <button type="button" className="hd-recording-remove" onClick={() => setReplyRecording(null)} aria-label="Remove">×</button>
                                </div>
                            )}

                            <button 
                                type="submit" 
                                className="hd-btn hd-btn-primary"
                                disabled={replyLoading || (!replyMessage.trim() && !replyRecording)}
                            >
                                {replyLoading ? '...' : isInternal ? t('internalNote') : t('sendReply')}
                            </button>
                        </form>
                    </div>

                    {/* Right Column - Ticket Info */}
                    <div className="hd-info-section">
                        <div className="hd-info-card">
                            <h3>{t('ticketInfo')}</h3>
                            
                            <div className="hd-info-row">
                                <label>{t('status')}</label>
                                <select
                                    value={editStatus}
                                    onChange={(e) => handleFieldChange('status', e.target.value)}
                                    className="hd-select"
                                >
                                    <option value="open">{t('statusOpen')}</option>
                                    <option value="assigned">{t('statusAssigned')}</option>
                                    <option value="in_progress">{t('statusInProgress')}</option>
                                    <option value="waiting_for_customer">{t('statusWaiting')}</option>
                                    <option value="solved_pending">{t('statusSolvedPending')}</option>
                                    <option value="resolved">{t('statusResolved')}</option>
                                    <option value="closed">{t('statusClosed')}</option>
                                </select>
                            </div>

                            <div className="hd-info-row">
                                <label>{t('priority')}</label>
                                <select
                                    value={editPriority}
                                    onChange={(e) => handleFieldChange('priority', e.target.value)}
                                    className="hd-select"
                                >
                                    <option value="low">{t('priorityLow')}</option>
                                    <option value="medium">{t('priorityMedium')}</option>
                                    <option value="high">{t('priorityHigh')}</option>
                                    <option value="critical">{t('priorityCritical')}</option>
                                </select>
                            </div>

                            <div className="hd-info-row">
                                <label>{t('category')}</label>
                                <select
                                    value={editCategory}
                                    onChange={(e) => handleFieldChange('category', e.target.value)}
                                    className="hd-select"
                                >
                                    <option value="">{t('none')}</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="hd-info-row">
                                <label>{t('department')}</label>
                                <select
                                    value={editDepartment}
                                    onChange={(e) => handleFieldChange('department', e.target.value)}
                                    className="hd-select"
                                >
                                    <option value="">{t('none')}</option>
                                    {departments.map(dept => (
                                        <option key={dept.id} value={dept.id}>
                                            {language === 'ar' && dept.name_ar ? dept.name_ar : dept.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="hd-info-row">
                                <label>{t('created')}</label>
                                <span>{formatDate(ticket.created_at)}</span>
                            </div>

                            <div className="hd-info-row">
                                <label>{t('updated')}</label>
                                <span>{formatDate(ticket.updated_at)}</span>
                            </div>
                            <div className="hd-info-row">
                                <label>{t('createdBy')}</label>
                                <span>
                                    {ticket.created_by_type === 'staff' 
                                        ? `${t('createdByStaff')}${ticket.created_by_staff_name ? ': ' + ticket.created_by_staff_name : ''}`
                                        : t('createdByClient')
                                    }
                                </span>
                            </div>

                            {/* Save Button */}
                            {hasChanges && (
                                <button 
                                    onClick={handleSaveChanges}
                                    className="hd-btn hd-btn-success hd-btn-full"
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            )}
                        </div>

                        <div className="hd-info-card">
                            <h3>{t('clientInfo')}</h3>
                            <div className="hd-client-detail">
                                {/* Name hidden */}
                                {/* Email hidden */}
                                {/* Phone hidden */}
                                <div className="hd-info-row">
                                    <label>{t('company')}</label>
                                    <input
                                        type="text"
                                        value={editCompany}
                                        onChange={(e) => handleFieldChange('company', e.target.value)}
                                        className="hd-input"
                                        placeholder={t('enterCompany')}
                                    />
                                </div>

                                {/* ERP SSO origin. The tenant's helpdesk account is
                                    shared by every user of that tenant, so THIS is the
                                    only place the individual who raised the ticket is
                                    visible. Renders only for ERP-originated tickets. */}
                                {ticket.origin_user_name && (
                                    <>
                                        <div className="hd-info-row" style={{ marginTop: 12, fontWeight: 600, opacity: 0.75 }}>
                                            <label>{t('erpOrigin')}</label>
                                            <span />
                                        </div>
                                        <div className="hd-info-row">
                                            <label>{t('erpUser')}</label>
                                            <span>
                                                {ticket.origin_user_name}
                                                {ticket.origin_user_email ? ` · ${ticket.origin_user_email}` : ''}
                                            </span>
                                        </div>
                                        {ticket.origin_subsidiary && (
                                            <div className="hd-info-row">
                                                <label>{t('erpSubsidiary')}</label>
                                                <span>{ticket.origin_subsidiary}</span>
                                            </div>
                                        )}
                                        {ticket.origin_erp_db && (
                                            <div className="hd-info-row">
                                                <label>{t('erpTenant')}</label>
                                                <span style={{ direction: 'ltr', unicodeBidi: 'plaintext' }}>{ticket.origin_erp_db}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            
                            {/* Save Button for client info */}
                            {hasChanges && (
                                <button 
                                    onClick={handleSaveChanges}
                                    className="hd-btn hd-btn-success hd-btn-full"
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            )}
                        </div>

                        {/* Attachments Card */}
                        {attachments.length > 0 && (
                            <div className="hd-info-card">
                                <h3>📎 {t('allAttachments')} ({attachments.length})</h3>
                                <ul className="hd-attachment-list-full">
                                    {attachments.map(att => (
                                        <li key={att.id} className="hd-attachment-item">
                                            <a href={getAttachmentUrl(att)} target="_blank" rel="noopener noreferrer">
                                                {att.file_type && att.file_type.startsWith('image/') ? '🖼️' : '📄'} {att.file_name}
                                            </a>
                                            <span className="hd-file-meta">
                                                {formatFileSize(att.file_size)} • {att.uploaded_by_type}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="hd-info-card">
                            <h3>{t('assignedAgents')}</h3>
                            {assignedAgents.length === 0 ? (
                                <p className="hd-no-agents">{t('noAgents')}</p>
                            ) : (
                                <ul className="hd-agent-list">
                                    {assignedAgents.map(agent => (
                                        <li key={agent.id || agent.agent_id}>
                                            <span>{agent.agent_name}</span>
                                            <button 
                                                onClick={() => handleUnassign(agent.agent_id)}
                                                className="hd-btn-remove"
                                                disabled={unassignLoading === agent.agent_id}
                                                title="Remove agent"
                                            >
                                                {unassignLoading === agent.agent_id ? '...' : '×'}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Edit History Card */}
                        <div className="hd-info-card">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h3>📝 {t('editHistory')}</h3>
                                <button 
                                    onClick={() => {
                                        setShowHistory(!showHistory);
                                        if (!showHistory && history.length === 0) {
                                            loadHistory();
                                        }
                                    }}
                                    style={{ 
                                        fontSize: '12px', 
                                        padding: '6px 12px',
                                        backgroundColor: showHistory ? '#ef4444' : '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {showHistory ? t('hide') : t('show')}
                                </button>
                            </div>
                            
                            {showHistory && (
                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {historyLoading ? (
                                        <p style={{ color: '#888', textAlign: 'center' }}>{t('loading')}</p>
                                    ) : history.length === 0 ? (
                                        <p style={{ color: '#888', textAlign: 'center', fontSize: '13px' }}>{t('noHistory')}</p>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {history.map(item => (
                                                <div 
                                                    key={item.id} 
                                                    style={{ 
                                                        background: 'rgba(255,255,255,0.05)', 
                                                        borderRadius: '8px', 
                                                        padding: '10px',
                                                        borderLeft: '3px solid #3b82f6'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                        <span style={{ 
                                                            background: 'rgba(59, 130, 246, 0.2)', 
                                                            color: '#60a5fa', 
                                                            padding: '2px 8px', 
                                                            borderRadius: '4px', 
                                                            fontSize: '11px',
                                                            fontWeight: 600
                                                        }}>
                                                            {getFieldLabel(item.field_name)}
                                                        </span>
                                                        <span style={{ color: '#888', fontSize: '11px' }}>
                                                            {formatDate(item.changed_at)}
                                                        </span>
                                                    </div>
                                                    <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                                                        <span style={{ color: '#ef4444' }}>- {truncateText(item.old_value, 80)}</span>
                                                    </div>
                                                    <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                                                        <span style={{ color: '#22c55e' }}>+ {truncateText(item.new_value, 80)}</span>
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                                        {t('by')}: {item.changed_by_name || t('unknown')}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Assign Modal */}
                {showAssignModal && (
                    <div className="hd-modal-overlay" onClick={() => !assignLoading && setShowAssignModal(false)}>
                        <div className="hd-modal" onClick={(e) => e.stopPropagation()}>
                            <h3>{t('assignAgent')}</h3>
                            <select
                                value={selectedAgent}
                                onChange={(e) => setSelectedAgent(e.target.value)}
                                className="hd-select"
                                disabled={assignLoading}
                            >
                                <option value="">{t('selectAgent')}</option>
                                {agents.map(agent => (
                                    <option key={agent.id} value={agent.id}>{agent.full_name}</option>
                                ))}
                            </select>
                            <div className="hd-modal-actions">
                                <button 
                                    onClick={() => setShowAssignModal(false)} 
                                    className="hd-btn hd-btn-secondary"
                                    disabled={assignLoading}
                                >
                                    {t('cancel')}
                                </button>
                                <button 
                                    onClick={handleAssignTicket} 
                                    className="hd-btn hd-btn-primary"
                                    disabled={assignLoading || !selectedAgent}
                                >
                                    {assignLoading ? t('loading') : t('assign')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="hd-modal-overlay" onClick={() => !deleteLoading && setShowDeleteModal(false)}>
                        <div className="hd-modal" onClick={(e) => e.stopPropagation()}>
                            <h3 style={{ color: '#ef4444' }}>⚠️ {t('deleteTicket')}</h3>
                            <p style={{ margin: '15px 0', color: '#ccc' }}>
                                Are you sure you want to delete ticket <strong>#{ticket.ticket_number}</strong>?
                            </p>
                            <p style={{ margin: '10px 0', color: '#f87171', fontSize: '14px' }}>
                                {t('confirmDeleteMessage')}
                            </p>
                            <div className="hd-modal-actions">
                                <button 
                                    onClick={() => setShowDeleteModal(false)} 
                                    className="hd-btn hd-btn-secondary"
                                    disabled={deleteLoading}
                                >
                                    {t('cancel')}
                                </button>
                                <button 
                                    onClick={handleDeleteTicket} 
                                    className="hd-btn"
                                    style={{ backgroundColor: '#ef4444', color: 'white' }}
                                    disabled={deleteLoading}
                                >
                                    {deleteLoading ? t('deleting') : `🗑️ ${t('delete')}`}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {showRecorder && (
                <ScreenRecorder
                    reserve={reserveRecording}
                    onRecorded={setReplyRecording}
                    onClose={() => setShowRecorder(false)}
                    language={language}
                    title={ticket ? `${ticket.ticket_number || ''} ${ticket.subject || ''}`.trim() : ''}
                    maxBytes={recordingConfig?.max_bytes}
                />
            )}
        </Layout>
    );
}

export default HelpdeskTicketDetail;